import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Tabs,
  Tab,
  Slider,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunAnalysisIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  Upload as ImportIcon,
  Palette as PaletteIcon,
  DeleteSweep as DeleteAllIcon
} from '@mui/icons-material';
import {
  PROJECT_GEOMETRY_COLOR_PRESETS,
  PROJECT_GEOMETRY_DEFAULT_COLOR,
  normalizeProjectGeometryColor,
} from '../../utils/projectGeometryColors';

interface PolygonManagerProps {
  projectId: string;
  polygons: PolygonRegion[];
  pendingPolygonData?: {coordinates: number[][], metadata: PolygonMetadata, isValidationFallback?: boolean, color?: string, transparency?: number} | null;
  onPolygonCreate: (polygon: CreatePolygonRequest) => void;
  onPolygonUpdate: (polygonId: string, updates: Partial<PolygonRegion>) => void;
  onPolygonDelete: (polygonId: string) => void;
  onPolygonVisibilityToggle: (polygonId: string, visible: boolean) => void;
  onRunAnalysis: (polygonId: string, analysisConfig: AnalysisConfig) => void;
  onExportPolygon: (polygonId: string, format: 'geojson' | 'kml') => void;
  onImportPolygons: (file: File, options?: { color?: string; transparency?: number }) => void;
  onDeleteAllPolygons?: () => void;
  onPendingPolygonCleared?: () => void;
}

interface PolygonRegion {
  id: string;
  name: string;
  coordinates: number[][];
  metadata: PolygonMetadata;
  region_type: 'project_boundary' | 'analysis_region';
  is_valid: boolean;
  validation_errors: string[];
  visible: boolean;
  color?: string;
  transparency?: number;
  created_at: string;
  updated_at: string;
}

interface PolygonMetadata {
  area_km2: number;
  perimeter_km: number;
  vertex_count: number;
  bounds: {
    min_lat: number;
    min_lon: number;
    max_lat: number;
    max_lon: number;
  };
}

interface CreatePolygonRequest {
  name: string;
  coordinates: number[][];
  region_type: 'project_boundary' | 'analysis_region';
  metadata: PolygonMetadata;
  color?: string;
  transparency?: number;
}

interface AnalysisConfig {
  commodity: 'gold' | 'silver' | 'copper';
  analysis_name: string;
  confidence_threshold: number;
  output_formats: string[];
}

const PolygonManager: React.FC<PolygonManagerProps> = ({
  projectId,
  polygons,
  pendingPolygonData,
  onPolygonCreate,
  onPolygonUpdate,
  onPolygonDelete,
  onPolygonVisibilityToggle,
  onRunAnalysis,
  onExportPolygon,
  onImportPolygons,
  onDeleteAllPolygons,
  onPendingPolygonCleared
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [analysisDialogOpen, setAnalysisDialogOpen] = useState(false);
  const [selectedPolygon, setSelectedPolygon] = useState<PolygonRegion | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [importColor, setImportColor] = useState(PROJECT_GEOMETRY_DEFAULT_COLOR);
  const [importTransparency, setImportTransparency] = useState(0.35);
  
  // Create dialog state
  const [newPolygonName, setNewPolygonName] = useState('');
  const [newPolygonType, setNewPolygonType] = useState<'project_boundary' | 'analysis_region'>('analysis_region');
  const [pendingCoordinates, setPendingCoordinates] = useState<number[][] | null>(null);
  const [pendingMetadata, setPendingMetadata] = useState<PolygonMetadata | null>(null);
  const [newPolygonColor, setNewPolygonColor] = useState(PROJECT_GEOMETRY_DEFAULT_COLOR);
  const [newPolygonTransparency, setNewPolygonTransparency] = useState(0.3);
  const resolvedImportColor = normalizeProjectGeometryColor(importColor);
  const resolvedNewPolygonColor = normalizeProjectGeometryColor(newPolygonColor);

  // Analysis dialog state
  const [analysisCommodity, setAnalysisCommodity] = useState<'gold' | 'silver' | 'copper'>('gold');
  const [analysisName, setAnalysisName] = useState('');
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
  const [outputFormats, setOutputFormats] = useState<string[]>(['heatmap', 'geojson']);

  // Handle pending polygon data from parent
  React.useEffect(() => {
    if (pendingPolygonData && !createDialogOpen) {
      setPendingCoordinates(pendingPolygonData.coordinates);
      setPendingMetadata(pendingPolygonData.metadata);
      if (pendingPolygonData.color) {
        setNewPolygonColor(normalizeProjectGeometryColor(pendingPolygonData.color));
      }
      if (pendingPolygonData.transparency !== undefined) setNewPolygonTransparency(pendingPolygonData.transparency);
      setCreateDialogOpen(true);
    }
  }, [pendingPolygonData, createDialogOpen]);

  // Filter polygons by type
  const projectBoundaries = polygons.filter(p => p.region_type === 'project_boundary');
  const analysisRegions = polygons.filter(p => p.region_type === 'analysis_region');

  // Handle polygon creation completion from drawing tool
  const handlePolygonDrawn = useCallback((coordinates: number[][], metadata: PolygonMetadata) => {
    setPendingCoordinates(coordinates);
    setPendingMetadata(metadata);
    setCreateDialogOpen(true);
  }, []);

  // Create new polygon
  const handleCreatePolygon = useCallback(() => {
    if (!newPolygonName.trim() || !pendingCoordinates || !pendingMetadata) {
      return;
    }

    const createRequest: CreatePolygonRequest = {
      name: newPolygonName.trim(),
      coordinates: pendingCoordinates,
      region_type: newPolygonType,
      metadata: pendingMetadata,
      color: resolvedNewPolygonColor,
      transparency: newPolygonTransparency
    };

    onPolygonCreate(createRequest);
    
    // Reset state
    setCreateDialogOpen(false);
    setNewPolygonName('');
    setNewPolygonType('analysis_region');
    setPendingCoordinates(null);
    setPendingMetadata(null);
    setNewPolygonColor(PROJECT_GEOMETRY_DEFAULT_COLOR);
    setNewPolygonTransparency(0.3);
    
    // Clear pending data from parent
    if (onPendingPolygonCleared) {
      onPendingPolygonCleared();
    }
    
    }, [
      newPolygonName,
      pendingCoordinates,
      pendingMetadata,
      newPolygonType,
      resolvedNewPolygonColor,
      newPolygonTransparency,
      onPolygonCreate,
      onPendingPolygonCleared
    ]);

  // Open edit dialog
  const handleEditPolygon = useCallback((polygon: PolygonRegion) => {
    setSelectedPolygon(polygon);
    setNewPolygonName(polygon.name);
    setNewPolygonType(polygon.region_type);
    setNewPolygonColor(normalizeProjectGeometryColor(polygon.color));
    setNewPolygonTransparency(polygon.transparency || 0.3);
    setEditDialogOpen(true);
  }, []);

  // Update polygon
  const handleUpdatePolygon = useCallback(() => {
    if (!selectedPolygon || !newPolygonName.trim()) return;

    const updates: Partial<PolygonRegion> = {
      name: newPolygonName.trim(),
      region_type: newPolygonType,
      color: resolvedNewPolygonColor,
      transparency: newPolygonTransparency
    };

    onPolygonUpdate(selectedPolygon.id, updates);
    
    // Reset state
    setEditDialogOpen(false);
    setSelectedPolygon(null);
    setNewPolygonName('');
    setNewPolygonType('analysis_region');
    setNewPolygonColor(PROJECT_GEOMETRY_DEFAULT_COLOR);
    setNewPolygonTransparency(0.3);
  }, [
    selectedPolygon,
    newPolygonName,
    newPolygonType,
    resolvedNewPolygonColor,
    newPolygonTransparency,
    onPolygonUpdate
  ]);

  // Open analysis dialog
  const handleOpenAnalysis = useCallback((polygon: PolygonRegion) => {
    setSelectedPolygon(polygon);
    setAnalysisName(`${polygon.name} Analysis`);
    setAnalysisDialogOpen(true);
  }, []);

  // Run analysis
  const handleRunAnalysis = useCallback(() => {
    if (!selectedPolygon || !analysisName.trim()) return;

    const config: AnalysisConfig = {
      commodity: analysisCommodity,
      analysis_name: analysisName.trim(),
      confidence_threshold: confidenceThreshold,
      output_formats: outputFormats
    };

    onRunAnalysis(selectedPolygon.id, config);
    
    // Reset state
    setAnalysisDialogOpen(false);
    setSelectedPolygon(null);
    setAnalysisName('');
  }, [selectedPolygon, analysisCommodity, analysisName, confidenceThreshold, outputFormats, onRunAnalysis]);

  // Handle file import
  const handleFileImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onImportPolygons(file, { color: resolvedImportColor, transparency: importTransparency });
    }
    // Reset input
    event.target.value = '';
  }, [onImportPolygons, resolvedImportColor, importTransparency]);

  // Format area for display
  const formatArea = (area_km2: number): string => {
    if (area_km2 >= 1000) {
      return `${(area_km2 / 1000).toFixed(1)}k km²`;
    }
    return `${area_km2.toFixed(1)} km²`;
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  // Color controls component
  const renderColorControls = () => (
    <Box mt={2}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          color: 'var(--palantir-text-secondary)',
          fontFamily: 'var(--palantir-font-mono)',
          fontSize: 'var(--palantir-text-xs)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <PaletteIcon sx={{ fontSize: 14 }} />
        Polygon Style
      </Typography>
      
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <Box>
          <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)', fontFamily: 'var(--palantir-font-mono)' }}>
            Color
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                backgroundColor: resolvedNewPolygonColor,
                border: '2px solid var(--palantir-border-default)',
                borderRadius: '6px',
                cursor: 'pointer',
                position: 'relative'
              }}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'color';
                input.value = resolvedNewPolygonColor;
                input.onchange = (e) => setNewPolygonColor(
                  normalizeProjectGeometryColor((e.target as HTMLInputElement).value)
                );
                input.click();
              }}
            >
              <input
                type="color"
                value={resolvedNewPolygonColor}
                onChange={(e) => setNewPolygonColor(normalizeProjectGeometryColor(e.target.value))}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
            </Box>
            <TextField
              value={newPolygonColor}
              onChange={(e) => setNewPolygonColor(e.target.value)}
              size="small"
              fullWidth
              sx={{ 
                '& .MuiInputBase-input': {
                  fontSize: '12px',
                  fontFamily: 'var(--palantir-font-mono)',
                  color: 'var(--palantir-text-primary)'
                }
              }}
            />
          </Box>
        </Box>
        
        <Box>
          <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)', fontFamily: 'var(--palantir-font-mono)' }}>
            Transparency: {Math.round(newPolygonTransparency * 100)}%
          </Typography>
          <Slider
            value={newPolygonTransparency}
            onChange={(_, value) => setNewPolygonTransparency(value as number)}
            min={0.1}
            max={0.8}
            step={0.1}
            sx={{
              mt: 1,
              color: resolvedNewPolygonColor,
              '& .MuiSlider-thumb': {
                backgroundColor: resolvedNewPolygonColor,
                border: '2px solid var(--palantir-bg-primary)'
              },
              '& .MuiSlider-track': {
                backgroundColor: resolvedNewPolygonColor
              }
            }}
          />
        </Box>
      </Box>
      
      {/* Color Presets */}
      <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
        {PROJECT_GEOMETRY_COLOR_PRESETS.map((preset) => (
          <Tooltip key={preset.name} title={preset.name}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: preset.color,
                border: resolvedNewPolygonColor === preset.color ? '3px solid var(--palantir-text-primary)' : '2px solid var(--palantir-border-subtle)',
                borderRadius: '4px',
                cursor: 'pointer',
                '&:hover': {
                  border: '3px solid var(--palantir-text-secondary)'
                }
              }}
              onClick={() => setNewPolygonColor(preset.color)}
            />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );

  const renderPolygonList = (polygonList: PolygonRegion[], title: string) => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ color: 'var(--palantir-text-primary)', fontFamily: 'var(--palantir-font-sans)' }}>
        {title} ({polygonList.length})
      </Typography>
      
      {polygonList.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No {title.toLowerCase()} defined yet.
        </Alert>
      ) : (
        <List dense>
          {polygonList.map((polygon) => (
            <ListItem key={polygon.id} divider>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: normalizeProjectGeometryColor(polygon.color),
                        borderRadius: '2px',
                        border: '1px solid var(--palantir-border-default)',
                        flexShrink: 0
                      }}
                    />
                    <Typography variant="subtitle2" component="span" sx={{ color: 'var(--palantir-text-primary)' }}>
                      {polygon.name}
                    </Typography>
                    <Chip
                      label={formatArea(polygon.metadata.area_km2)}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        color: 'var(--palantir-text-muted)',
                        borderColor: 'var(--palantir-border-subtle)',
                        fontSize: '10px'
                      }}
                    />
                    {!polygon.is_valid && (
                      <Chip
                        label="Invalid"
                        size="small"
                        color="error"
                        sx={{ fontSize: '10px' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <React.Fragment>
                    <Typography variant="body2" component="span" display="block" sx={{ color: 'var(--palantir-text-muted)', fontSize: '12px' }}>
                      {polygon.metadata.vertex_count} vertices • Created {formatDate(polygon.created_at)}
                      {polygon.transparency && ` • ${Math.round(polygon.transparency * 100)}% opacity`}
                    </Typography>
                    {!polygon.is_valid && polygon.validation_errors.length > 0 && (
                      <Typography variant="caption" component="span" display="block" sx={{ color: 'var(--palantir-status-error)', fontSize: '11px' }}>
                        {polygon.validation_errors[0]}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
              
              <ListItemSecondaryAction>
                <Box display="flex" gap={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => onPolygonVisibilityToggle(polygon.id, !polygon.visible)}
                    color={polygon.visible ? 'primary' : 'default'}
                  >
                    {polygon.visible ? <ViewIcon /> : <HideIcon />}
                  </IconButton>
                  
                  <IconButton
                    size="small"
                    onClick={() => handleEditPolygon(polygon)}
                  >
                    <EditIcon />
                  </IconButton>
                  
                  {polygon.region_type === 'analysis_region' && polygon.is_valid && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenAnalysis(polygon)}
                      color="success"
                    >
                      <RunAnalysisIcon />
                    </IconButton>
                  )}
                  
                  <IconButton
                    size="small"
                    onClick={() => onPolygonDelete(polygon.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Header with actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" sx={{ color: 'var(--palantir-text-primary)', fontFamily: 'var(--palantir-font-sans)' }}>
          Polygon Regions
        </Typography>
        
        <Box display="flex" gap={1} alignItems="center">
          <Box display="flex" flexDirection="column" mr={1}>
            <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)', fontFamily: 'monospace' }}>Import color</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <input
                type="color"
                value={resolvedImportColor}
                onChange={(e) => setImportColor(normalizeProjectGeometryColor(e.target.value))}
                style={{ width: 32, height: 32, border: 'none', background: 'transparent', cursor: 'pointer' }}
              />
              <Box display="flex" flexDirection="column">
                <Typography variant="caption" sx={{ color: 'var(--palantir-text-muted)', fontFamily: 'monospace' }}>
                  Opacity {Math.round(importTransparency * 100)}%
                </Typography>
                <Slider
                  value={importTransparency}
                  onChange={(_, value) => setImportTransparency(value as number)}
                  min={0.1}
                  max={0.9}
                  step={0.05}
                  sx={{ width: 120 }}
                />
              </Box>
            </Box>
          </Box>
          <input
            accept=".geojson,.kml,.json"
            style={{ display: 'none' }}
            id="import-file"
            type="file"
            onChange={handleFileImport}
          />
          <label htmlFor="import-file">
            <Button
              component="span"
              startIcon={<ImportIcon />}
              variant="outlined"
              size="small"
            >
              Import
            </Button>
          </label>
          <Button
            startIcon={<DeleteAllIcon />}
            variant="outlined"
            size="small"
            color="error"
            disabled={!polygons.length}
            onClick={() => setDeleteAllDialogOpen(true)}
          >
            Delete All
          </Button>
        </Box>
      </Box>

      {/* Tabs for different polygon types */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label="Analysis Regions" />
        <Tab label="Project Boundaries" />
        <Tab label="All Regions" />
      </Tabs>

      {/* Tab content */}
      {activeTab === 0 && renderPolygonList(analysisRegions, 'Analysis Regions')}
      {activeTab === 1 && renderPolygonList(projectBoundaries, 'Project Boundaries')}
      {activeTab === 2 && renderPolygonList(polygons, 'All Regions')}

      <Dialog
        open={deleteAllDialogOpen}
        onClose={() => setDeleteAllDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete All Regions?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--palantir-text-primary)' }}>
            This will permanently remove all {polygons.length} regions from this project.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setDeleteAllDialogOpen(false);
              onDeleteAllPolygons?.();
            }}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Polygon Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Polygon Region</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your polygon has been drawn! Please provide a name to save it as a region for analysis.
            </Alert>
            <TextField
              label="Region Name"
              value={newPolygonName}
              onChange={(e) => setNewPolygonName(e.target.value)}
              fullWidth
              autoFocus
              placeholder="e.g., Mining Prospect Area 1"
              sx={{
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiInputBase-input': { color: 'var(--palantir-text-primary)' },
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--palantir-border-default)' }
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'var(--palantir-text-muted)' }}>Region Type</InputLabel>
              <Select
                value={newPolygonType}
                onChange={(e) => setNewPolygonType(e.target.value as any)}
                label="Region Type"
                sx={{
                  '& .MuiSelect-select': { color: 'var(--palantir-text-primary)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--palantir-border-default)' }
                }}
              >
                <MenuItem value="analysis_region">Analysis Region</MenuItem>
                <MenuItem value="project_boundary">Project Boundary</MenuItem>
              </Select>
            </FormControl>

            {renderColorControls()}

            {pendingMetadata && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Polygon Information
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip label={formatArea(pendingMetadata.area_km2)} size="small" />
                  <Chip label={`${pendingMetadata.perimeter_km.toFixed(1)} km perimeter`} size="small" />
                  <Chip label={`${pendingMetadata.vertex_count} vertices`} size="small" />
                </Box>
                
                {/* Show notice if using fallback calculations */}
                {pendingPolygonData?.isValidationFallback && (
                  <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
                    <Typography variant="caption">
                      📊 Using approximate area calculations (validation service unavailable). 
                      Final measurements will be calculated accurately when saved.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreatePolygon}
            variant="contained"
            disabled={!newPolygonName.trim()}
          >
            Create Region
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Polygon Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Polygon Region</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Region Name"
              value={newPolygonName}
              onChange={(e) => setNewPolygonName(e.target.value)}
              fullWidth
              autoFocus
              sx={{
                '& .MuiInputLabel-root': { color: 'var(--palantir-text-muted)' },
                '& .MuiInputBase-input': { color: 'var(--palantir-text-primary)' },
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--palantir-border-default)' }
              }}
            />
            
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'var(--palantir-text-muted)' }}>Region Type</InputLabel>
              <Select
                value={newPolygonType}
                onChange={(e) => setNewPolygonType(e.target.value as any)}
                label="Region Type"
                sx={{
                  '& .MuiSelect-select': { color: 'var(--palantir-text-primary)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--palantir-border-default)' }
                }}
              >
                <MenuItem value="analysis_region">Analysis Region</MenuItem>
                <MenuItem value="project_boundary">Project Boundary</MenuItem>
              </Select>
            </FormControl>

            {renderColorControls()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdatePolygon}
            variant="contained"
            disabled={!newPolygonName.trim()}
          >
            Update Region
          </Button>
        </DialogActions>
      </Dialog>

      {/* Analysis Configuration Dialog */}
      <Dialog open={analysisDialogOpen} onClose={() => setAnalysisDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run Mineral Discovery Analysis</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Analysis Name"
              value={analysisName}
              onChange={(e) => setAnalysisName(e.target.value)}
              fullWidth
              autoFocus
            />
            
            <FormControl fullWidth>
              <InputLabel>Target Commodity</InputLabel>
              <Select
                value={analysisCommodity}
                onChange={(e) => setAnalysisCommodity(e.target.value as any)}
                label="Target Commodity"
              >
                <MenuItem value="gold">Gold</MenuItem>
                <MenuItem value="silver">Silver</MenuItem>
                <MenuItem value="copper">Copper</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Confidence Threshold: {(confidenceThreshold * 100).toFixed(0)}%
              </Typography>
              <input
                type="range"
                min="0.5"
                max="0.95"
                step="0.05"
                value={confidenceThreshold}
                onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </Box>

            {selectedPolygon && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Analysis Area: {selectedPolygon.name}
                </Typography>
                <Chip label={formatArea(selectedPolygon.metadata.area_km2)} size="small" />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnalysisDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRunAnalysis}
            variant="contained"
            startIcon={<RunAnalysisIcon />}
            disabled={!analysisName.trim()}
          >
            Run Analysis
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PolygonManager;
